import type { StoredUsers } from '~/gql/users/users'
import type { UserAction } from '~/pages/backoffice/admin/user/list/user-actions'
import { useQuery } from '@apollo/client/react'
import { RoleEnum, TrainerCapability } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import Spinner from '~/components/Spinner'
import { Card, CardContent, CardHeader } from '~/components/ui/card'
import { GQL_GROUPS } from '~/gql/groups/groups'
import { useAuth } from '~/helpers/store/auth'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useCan } from '~/hooks/use-can'
import { useCredentialsFlow } from '~/pages/backoffice/admin/user/credentials/use-credentials-flow'
import BulkGroupBar from '~/pages/backoffice/admin/user/list/BulkGroupBar'
import { useBulkGroupAssignment } from '~/pages/backoffice/admin/user/list/use-bulk-group-assignment'
import { useResendInvitation } from '~/pages/backoffice/admin/user/list/use-resend-invitation'
import { useUserDialogs } from '~/pages/backoffice/admin/user/list/use-user-dialogs'
import { useUserFilters } from '~/pages/backoffice/admin/user/list/use-user-filters'
import { useUserSelection } from '~/pages/backoffice/admin/user/list/use-user-selection'
import { useUsersExport } from '~/pages/backoffice/admin/user/list/use-users-export'
import { useUsersPage } from '~/pages/backoffice/admin/user/list/use-users-page'
import { availableUserActions } from '~/pages/backoffice/admin/user/list/user-actions'
import UserDialogs from '~/pages/backoffice/admin/user/list/UserDialogs'
import UserRowActions from '~/pages/backoffice/admin/user/list/UserRowActions'
import UsersList from '~/pages/backoffice/admin/user/list/UsersList'
import UsersToolbar from '~/pages/backoffice/admin/user/list/UsersToolbar'
import UsersToolbarActions from '~/pages/backoffice/admin/user/list/UsersToolbarActions'
import { NoResults } from '~/pages/backoffice/NoResults'
import ResultsStatus from '~/pages/backoffice/ResultsStatus'
import TablePagination from '~/pages/backoffice/TablePagination'
import { useTableQuery } from '~/pages/backoffice/useTableQuery'

/**
 * Les comptes de l'organisation, paginés, triés et filtrés côté serveur. Seul
 * l'admin y écrit (création, import, groupes, menu de ligne) : le formateur,
 * qui partage l'écran depuis #201, le lit et l'exporte — cf. `availableUserActions`.
 */
function UserTable() {
  const { t } = useTranslation()
  const { payload } = useAuth()
  const isAdmin = payload?.role === RoleEnum.Admin
  const canViewSessions = useCan(TrainerCapability.SESSIONS_VIEW)
  const table = useTableQuery()
  const { page, pageSize, setPage, setPageSize, resetPage, handleSort, sortedInfo } = table
  // Avant `useUserFilters` : son `onChange` vide la sélection dès le premier rendu.
  const selection = useUserSelection()
  const filters = useUserFilters(() => {
    resetPage()
    selection.clear()
  })
  const dialogs = useUserDialogs()
  const credentials = useCredentialsFlow()
  const resendInvitation = useResendInvitation()

  const { data: groupsData, refetch: refetchGroups } = useQuery(GQL_GROUPS)
  const groups = groupsData?.groups ?? []
  const { users, total, loaded, loading, refetch } = useUsersPage(table, filters)

  const bulk = useBulkGroupAssignment(selection.selectedIds, async () => {
    selection.clear()
    await Promise.all([refetch(), refetchGroups()])
  })
  // Le fichier porte le nom de l'écran : « Apprenants » pour le formateur, qui
  // n'exporte pas la même population que l'admin.
  const exportCsv = useUsersExport(filters, isAdmin ? t('common.users') : t('common.learners'))

  const onAction = (action: UserAction, user: StoredUsers) => {
    if (action === 'edit') dialogs.show('form', user)
    else if (action === 'resendInvitation') void resendInvitation(user)
    else if (action === 'resetCredentials') credentials.startReset({ id: user.id, username: user.username ?? '', firstName: user.firstName, lastName: user.lastName })
    else dialogs.show(action, user)
  }

  // Changer la tranche affichée vide la sélection : on ne coche que ce qu'on voit.
  const changePage = (next: number) => {
    setPage(next)
    selection.clear()
  }
  const changePageSize = (next: number) => {
    setPageSize(next)
    selection.clear()
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <Card>
        <CardHeader className="gap-4">
          <UsersToolbar
            filters={filters}
            groups={groups}
            onRefresh={() => void refetch()}
            actions={(
              <UsersToolbarActions
                onAdd={isAdmin ? () => dialogs.show('form') : undefined}
                onImport={isAdmin ? () => dialogs.show('import') : undefined}
                onExport={() => void exportCsv()}
              />
            )}
          />
          {isAdmin && selection.selectedIds.size > 0 && (
            <BulkGroupBar
              count={selection.selectedIds.size}
              groups={groups}
              groupId={bulk.groupId}
              onGroupChange={bulk.setGroupId}
              onApply={action => void bulk.apply(action)}
              onClear={selection.clear}
            />
          )}
          <ResultsStatus count={total} filtering={filters.filtering && !loading} />
        </CardHeader>
        <CardContent {...tourAnchor('users.table')}>
          {!loaded
            ? <div className="flex justify-center p-6"><Spinner className="size-6" /></div>
            : total === 0
              ? filters.filtering
                ? <NoResults query={filters.query} onReset={filters.reset} />
                : <p className="p-4 text-center text-sm text-muted-foreground">{t('users.empty')}</p>
              : (
                  <>
                    <UsersList
                      users={users}
                      currentUserId={payload?.id}
                      sortedInfo={sortedInfo}
                      onSort={handleSort}
                      // Cocher ne sert qu'à l'affectation en masse, réservée à l'admin.
                      selection={isAdmin ? selection : undefined}
                      renderActions={user => (
                        <UserRowActions
                          user={user}
                          actions={availableUserActions({ id: payload?.id, role: payload?.role, canViewSessions }, user)}
                          onAction={onAction}
                        />
                      )}
                    />
                    <TablePagination page={page} pageSize={pageSize} total={total} onPageChange={changePage} onPageSizeChange={changePageSize} />
                  </>
                )}
        </CardContent>
      </Card>

      <UserDialogs dialogs={dialogs} onChanged={() => void refetch()} onIssued={credentials.showIssued} />
      {credentials.dialogs}
    </div>
  )
}

export default UserTable
